import React from 'react';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { MultiColumnVizProps, getRowValue, useChartTheme } from './types';
import { dsVar } from '../../theme/tokens';

// ─── Tooltip ─────────────────────────────────────────────────────────────────

interface BubbleTooltipProps {
  active?: boolean;
  payload?: any[];
  headers: string[];
  title: string;
  safeValue: (val: any) => React.ReactNode;
}

/**
 * Custom tooltip for the scatter/bubble chart.
 * Defined at module level (not inside render) to avoid React reconciliation issues.
 */
const BubbleTooltip: React.FC<BubbleTooltipProps> = ({
  active,
  payload,
  headers,
  title,
  safeValue,
}) => {
  if (!active || !payload?.length) return null;
  const dataPoint = payload[0].payload;
  return (
    <div className="bg-surface border border-line rounded-lg shadow-xl p-3 text-xs">
      <p className="font-bold text-accent-fg mb-2">{title}</p>
      {headers.map((h, i) => (
        <div key={i} className="flex justify-between gap-4 mb-1 last:mb-0">
          <span className="text-fg-2">{h}:</span>
          <span style={{ color: dsVar('fg-body'), fontFamily: '"Inter", sans-serif' }}>{safeValue(dataPoint[h])}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Chart Component ──────────────────────────────────────────────────────────

/**
 * Renders a Recharts bubble/scatter chart.
 * Triggered by: visualizationType = 'SCATTER_BUBBLE'
 *
 * Reads axis labels and data-key params from metadata, with fallback to header names.
 */
const ScatterBubbleChart: React.FC<MultiColumnVizProps> = ({
  headers,
  data,
  metadata,
  itemLabel,
  safeValue,
}) => {
    const theme = useChartTheme();
  const xLabel = metadata.x_axis_label || headers[0];
  const yLabel = metadata.y_axis_label || headers[1];
  const zLabel = metadata.bubble_label || headers[2];

  const xParam = metadata.x_axis_param;
  const yParam = metadata.y_axis_param;
  const zParam = metadata.bubble_size_param;

  const chartTitle = metadata.title || itemLabel || 'Data Point';

  const chartData = data.map((row: any) => {
    const obj: any = {};
    obj.x = getRowValue(row, xLabel, xParam, 0);
    obj.y = getRowValue(row, yLabel, yParam, 1);
    obj.z = getRowValue(row, zLabel, zParam, 2);
    obj.label = getRowValue(row, zLabel, undefined, 0);
    // Preserve all original fields for the tooltip
    headers.forEach((h, i) => {
      obj[h] = getRowValue(row, h, undefined, i);
    });
    return obj;
  });

  return (
    <div className="mb-8 p-6 bg-surface border border-line rounded-xl">
      {(metadata.title || itemLabel) && (
        <h3 className="text-accent-fg text-base font-mono mb-6 flex items-center gap-2">
          <AnalyticsIcon fontSize="small" />
          {chartTitle}
        </h3>
      )}
      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.border.default}
              vertical={false}
            />
            <XAxis
              type="number"
              dataKey="x"
              name={xLabel}
              stroke={theme.text.muted}
              fontSize={12}
              tick={{ fontFamily: '"Inter", sans-serif' }}
              label={{
                value: xLabel,
                position: 'bottom',
                fill: theme.text.muted,
                fontSize: 12,
                offset: 0,
                fontFamily: '"Inter", sans-serif'
              }}
              domain={['auto', 'auto']}
            />
            <YAxis
              type="number"
              dataKey="y"
              name={yLabel}
              stroke={theme.text.muted}
              fontSize={12}
              tick={{ fontFamily: '"Inter", sans-serif' }}
              label={{
                value: yLabel,
                angle: -90,
                position: 'left',
                fill: theme.text.muted,
                fontSize: 12,
                fontFamily: '"Inter", sans-serif'
              }}
              domain={['auto', 'auto']}
            />
            <ZAxis
              type="number"
              dataKey="z"
              range={[100, 1000]}
              name={zLabel}
            />
            <Tooltip
              content={
                <BubbleTooltip
                  headers={headers}
                  title={chartTitle}
                  safeValue={safeValue}
                />
              }
              cursor={{ strokeDasharray: '3 3', stroke: theme.text.accent }}
            />
            <Scatter name="Insights" data={chartData} fill={theme.text.accent}>
              {chartData.map((_entry: any, idx: number) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={idx % 2 === 0 ? theme.bubble.primary : theme.bubble.secondary}
                  fillOpacity={0.6}
                  stroke={idx % 2 === 0 ? theme.bubble.primary : theme.bubble.secondary}
                  strokeWidth={1}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ScatterBubbleChart;
